/**
 * Desmond Keyboard Remapper
 * Intercepts keyboard events and remaps them to Desmond's expected keyCodes.
 *
 * Desmond uses window.onkeydown/onkeyup with e.keyCode (legacy).
 * This remapper sits between the browser and Desmond's handler,
 * translating custom user bindings into the keyCodes Desmond expects.
 */

import { DSButton } from './cores/IEmulatorCore';
import { KeyMapping, loadKeyMapping } from './InputManager';

// Desmond's hardcoded keyCode for each DS button
// Source: desmond.js line 2944 — emuKeyboradMapping array
const DS_BUTTON_TO_DESMOND_KEYCODE: Record<number, number> = {
  [DSButton.Right]: 39,   // ArrowRight
  [DSButton.Left]: 37,    // ArrowLeft
  [DSButton.Down]: 40,    // ArrowDown
  [DSButton.Up]: 38,      // ArrowUp
  [DSButton.Select]: 16,  // Shift
  [DSButton.Start]: 13,   // Enter
  [DSButton.B]: 90,       // Z
  [DSButton.A]: 88,       // X
  [DSButton.Y]: 65,       // A
  [DSButton.X]: 83,       // S
  [DSButton.L]: 81,       // Q
  [DSButton.R]: 87,       // W
};

type KeyHandler = ((e: any) => void) | null;

export class DesmondKeyboardRemapper {
  private originalOnKeyDown: KeyHandler = null;
  private originalOnKeyUp: KeyHandler = null;
  private installed = false;
  // Precomputed: event.code → desmond keyCode
  private codeToKeyCode: Map<string, number> = new Map();

  constructor() {
    this.rebuildLookup(loadKeyMapping());
  }

  /**
   * Rebuild the event.code → desmond keyCode lookup table
   */
  rebuildLookup(mapping: KeyMapping): void {
    this.codeToKeyCode.clear();
    for (const [code, dsButton] of Object.entries(mapping)) {
      const desmondKeyCode = DS_BUTTON_TO_DESMOND_KEYCODE[dsButton];
      if (desmondKeyCode !== undefined) {
        this.codeToKeyCode.set(code, desmondKeyCode);
      }
    }
  }

  /**
   * Install the remapper by wrapping Desmond's window.onkeydown/onkeyup
   * Polls until Desmond has set its handlers (max 5 seconds)
   */
  async install(): Promise<void> {
    if (this.installed) return;

    // Wait for Desmond to set window.onkeydown/onkeyup
    console.log('⏳ Waiting for Desmond to set keyboard handlers...');
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds (50 * 100ms)

    while ((!window.onkeydown || !window.onkeyup) && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }

    if (!window.onkeydown || !window.onkeyup) {
      console.warn('⚠️ Desmond did not set keyboard handlers after 5s. Keyboard remapping may not work.');
      return;
    }

    console.log(`✅ Desmond keyboard handlers detected after ${attempts * 100}ms`);

    this.originalOnKeyDown = window.onkeydown;
    this.originalOnKeyUp = window.onkeyup;

    const handler = (e: KeyboardEvent) => {
      const originalHandler = e.type === 'keydown'
        ? this.originalOnKeyDown
        : this.originalOnKeyUp;

      if (!originalHandler) return;

      // ESC passes through directly (Desmond menu toggle)
      if (e.keyCode === 27) {
        originalHandler(e);
        return;
      }

      const targetKeyCode = this.codeToKeyCode.get(e.code);
      if (targetKeyCode !== undefined) {
        // Create a minimal fake event with the remapped keyCode
        const fakeEvent = {
          type: e.type,
          keyCode: targetKeyCode,
          preventDefault: () => e.preventDefault(),
        };
        originalHandler(fakeEvent);
      }
      // Keys not in the mapping are blocked (don't reach Desmond)
    };

    window.onkeydown = handler as any;
    window.onkeyup = handler as any;
    this.installed = true;
  }

  /**
   * Uninstall the remapper and restore Desmond's original handlers
   */
  uninstall(): void {
    if (!this.installed) return;

    window.onkeydown = this.originalOnKeyDown;
    window.onkeyup = this.originalOnKeyUp;
    this.originalOnKeyDown = null;
    this.originalOnKeyUp = null;
    this.installed = false;
  }
}
